import React, { useState, useEffect, useCallback, useContext } from 'react'
import { PropTypes } from 'prop-types'
import { navigate } from 'gatsby';
import { productItems, fusion4Tags, STYLE_GUIDE_TAG } from 'config/productItems';
import { NotificationContext, initialNotificationState } from 'context/notificationContextWrapper';
import VersionSelector from 'components/VersionSelector/VersionSelector.component';
import { HOW_TO, LEGACY, getBasePath } from 'utils/createURL';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs.component';
import {  SideBarContext, SideBarDispatchContext, initialSideBarState, UPDATE_GLOBAL_VERSION, UPDATE_GLOBAL_PRODUCT } from 'context/sidebarContextWrapper';
import PermalinkCopy from 'components/PermalinkCopy/PermalinkCopy.component'
import { browserHead, topbar,   selectorWrap  } from './topbar-styles.module.scss'


// Helper funciton for 'productDropdownItems' to generate proper sidebar naming for Fusion 4 (Fusion Server 4.x -> Fusion 4.x), and all product landing page links
const getVersionOptions = (name, versionsList, link, tag) => {
  if (name === 'Springboard'){
    return versionsList.map(ver => ({label: ver.replace('-', ' '), value: ver.replace('-', ' '), link: link.replace('$version', ver), tag}))
  }
  return versionsList.map(ver => ({label: ver, value: `${name.replace(' Server', '')} ${ver}`, link: link.replace('$version', ver), tag}))
}

const visibleProducts = productItems.filter(prod => !prod.hidden)

// Use productItems to generate nested product/version selector values for sidebar
const productDropdownItems = visibleProducts.reduce((optionsArray, prod) => { 
  const isInOptionsArray = optionsArray.some(obj => (obj.label === prod.subMenu))
  if(!isInOptionsArray){
    if(prod.subMenu) return ([...optionsArray, {label: prod.subMenu, options: getVersionOptions(prod.name, prod.versions, prod.link, prod.tag).reverse(), tag: prod.tag}]);
    if(prod.versions) return ([...optionsArray, {label: prod.name, options: getVersionOptions(prod.name, prod.versions, prod.link, prod.tag).reverse(), tag: prod.tag}]);
    if(prod.apptypes) return ([...optionsArray, {label: prod.name, options: getVersionOptions(prod.name, prod.apptypes, prod.link, prod.tag).reverse(), tag: prod.tag}]);
    return [...optionsArray, {label: prod.name, value: prod.name, link: prod.link, tag: prod.tag }]
  } 
  return optionsArray;
}, [])


const TopBarContent = React.memo(function TopBarContent({
  title = null,
  path
}){

    const productState = useContext(SideBarContext) || initialSideBarState;

    const dispatch = useContext(SideBarDispatchContext);
    const version = productState.currentVersion;

    const basePath = getBasePath(path);

    const handleGlobalVersionChange = useCallback((newProduct) => {
      dispatch({
        type: UPDATE_GLOBAL_VERSION, 
        payload: newProduct.label.replace(' ', '-'),
      });
      dispatch({
        type: UPDATE_GLOBAL_PRODUCT,
        payload: newProduct
      })
      
      if(newProduct.tag === LEGACY || newProduct.tag === STYLE_GUIDE_TAG){
        navigate(newProduct.link)
        return;
      }
      if(path !== "/" && basePath !== HOW_TO && basePath !== newProduct.tag && (!fusion4Tags.includes(basePath) || !fusion4Tags.includes(newProduct.tag))){
        navigate(newProduct.link);
      } else if (path !== '/' && path !== '/how-to'){
        const newURL = path.replace(`/${version}/`, `/${newProduct.label.replace(' ', '-')}/`);
        navigate(newURL)
      }
    }, [basePath, dispatch, path, version]);

  return (
    <>
      <Breadcrumbs 
        title={title}
        currentProductValue={productState.currentProduct.value}
        currentProductLink={productState.currentProduct.link}
        isHowTo={basePath === HOW_TO}
        basePath={basePath}
        path={path}
      />

      <div style={{marginRight: '1rem', minWidth:'223px', height:'fit-content', display:'flex', justifyContent:'flex-end'}}>
        <span className={selectorWrap} >
          <VersionSelector 
            handleChange={handleGlobalVersionChange}
            selectedOption={productState.currentProduct}
            options={productDropdownItems}
            display="full"
          />
        </span>

        {/* <button type='button' id="tb-userback" onClick={() => {
          console.log('loading userback...');
          window.Userback.open()
          // getUserback().open('bug', 'screenshot');
          // return Userback.show(); 
          // return Userback.open('bug', 'screenshot');
        }
          } style={{background: 'none', border: 'none', marginRight: '1rem'}}><span style={{color: 'var(--widget-button-bg)'}}>Feedback</span></button> */}


        <PermalinkCopy
          path={path}
        />
      </div>
    </>
  )
})

TopBarContent.propTypes = {
  title: PropTypes.string,
  path: PropTypes.string.isRequired
};

function TopBar({
    children,
    toggleSearchBar,
    path,
    title = null
}) { 

    const state = useContext(NotificationContext) || initialNotificationState;


    const [prevScrollPos, setPrevScrollPos] = useState(0);
    const [visible, setVisible] = useState(true);

    

    const handleScroll = useCallback( () => {
        if (typeof window !== 'undefined') { 
          if (window.scrollY > prevScrollPos && window.innerWidth > 767 && !toggleSearchBar && !state.isOpen) { // if scroll down hide the navbar
            setVisible(false); 
          } else { // if scroll up show the navbar
            setVisible(true);  
          }
    
          // remember current page location to use in the next move
          setPrevScrollPos(window.scrollY); 
        }
      }, [prevScrollPos, toggleSearchBar, state.isOpen]);
    
      useEffect(() => {
          window.addEventListener('scroll', handleScroll);
    
          return () => {
            window.removeEventListener('scroll', handleScroll);
          };
      }, [prevScrollPos, handleScroll]);

      useEffect(()=> {
        if(toggleSearchBar){
          setVisible(true)
        }
      }, [toggleSearchBar])

        return(
          <>
            <div className={`${browserHead} announcement-helper`} style={{top: visible ? '0' : '-60px'}}>
                {children}
            </div>
            <div className={`${topbar} ${visible ? 'topbarSpace' : 'topbarNoSpace'}`} style={{transition: 'top 100ms',}}>
              <TopBarContent
                title={title}
                path={path}
              />
            </div>
          </>
        )
    }

TopBar.propTypes = {
    children: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element), PropTypes.bool]),
    ).isRequired,
    toggleSearchBar: PropTypes.bool.isRequired,
    path: PropTypes.string.isRequired,
    title: PropTypes.string
  };
  
  export default TopBar;
  